import {
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconDeviceIpadPlus, IconPencil, IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useState } from "react";
import { CategoryList, PayableCreate } from "../services/payable";

import { useUserStore } from "../stores/user";
import { formatPrice, maskMoney, unMaskMoney } from "../helpers/format";
import Loading from "./Loading";
import CategoryRegister from "./CategoryRegister";
import CategoryEdit from "./CategoryEdit";

const PayableRegister = ({ open, onClose, refetch }: any) => {
  const [step, setStep] = useState("1");
  const [maskedValue, setMaskedValue] = useState("");
  const [openCategory, setOpenCategory] = useState(false);
  const [openEditCategory, setEditCategory] = useState(false);

  const { user } = useUserStore();
  const storeId = user?.storeId;

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchCategory,
  } = useQuery({
    queryKey: ["category"],
    queryFn: async () => await CategoryList(),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: PayableCreate,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Conta registrada com sucesso!");
    },
    onError: (e: any) => {
      const message =
        e?.response?.data?.message ??
        e?.message ??
        "Erro ao registrar a conta!";
      toast.error(message);
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue } = useFormik({
    initialValues: {
      title: "",
      description: "",
      amount: "",
      dueDate: "",
      categoryId: "",
      isRecurring: false,
      recurrence: "MONTHLY",
      status: "PENDING",
    },
    onSubmit: (values) => {
      const isRecurring = values.isRecurring;
      const recurrence = isRecurring ? values.recurrence : "ONETIME";
      const status =
        isRecurring && values.status !== "PAID" ? "SCHEDULED" : values.status;

      const payload: any = {
        ...values,
        storeId,
        amount: Number(unMaskMoney(maskedValue)),
        isRecurring,
        recurrence,
        status,
      };

      if (status === "PAID") {
        payload.paymentDate = new Date();
      }

      mutate(payload);
    },
  });

  return (
    <Dialog maxWidth="xs" open={open} onClose={onClose}>
      <Loading loading={isPending} />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={{ xs: 10 }}>
            <Typography fontSize={24} fontWeight={600}>
              Registrar Conta
            </Typography>
            <Typography variant="subtitle1" color="secondary.contrastText">
              Preencha os dados da conta a pagar
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2 }}>
            <IconButton onClick={onClose}>
              <IconX />
            </IconButton>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Tabs
              value={step}
              onChange={(_, v) => setStep(v)}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Geral" value="1" />
              <Tab label="Financeiro" value="2" disabled={step < "2"} />
              <Tab label="Recorrência" value="3" disabled={step < "3"} />
            </Tabs>
          </Grid2>

          {step === "1" && (
            <>
              <Grid2 size={{ xs: 12 }}>
                <Input
                  id="title"
                  name="title"
                  label="Título"
                  placeholder="Digite o título da conta"
                  value={values.title}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Input
                  id="description"
                  name="description"
                  label="Descrição"
                  placeholder="Descreva a conta"
                  multiline
                  rows={3}
                  value={values.description}
                  onChange={handleChange}
                />
              </Grid2>
            </>
          )}

          {step === "2" && (
            <>
              <Grid2 size={{ xs: 12 }}>
                <Stack>
                  <Typography fontWeight={600} fontSize={14}>
                    Valor
                  </Typography>

                  <Input
                    id="amount"
                    name="amount"
                    sx={{ marginTop: 1 }}
                    placeholder="Digite o valor"
                    value={formatPrice(maskedValue)}
                    onChange={(e) => {
                      const masked = maskMoney(e.target.value);
                      setMaskedValue(masked);
                      const numeric = unMaskMoney(masked);
                      setFieldValue("amount", numeric);
                    }}
                    onBlur={() => {
                      if (!maskedValue) return;
                      const numeric = unMaskMoney(maskedValue);
                      setFieldValue("amount", numeric);
                    }}
                  />
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Stack>
                  <Stack
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography fontWeight={600} fontSize={14}>
                      Categoria
                    </Typography>

                    <Stack direction={"row"} gap={1}>
                      <Tooltip title="Cadastrar categoria">
                        <IconButton onClick={() => setOpenCategory(true)}>
                          <IconDeviceIpadPlus size={14} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Editar categorias">
                        <IconButton onClick={() => setEditCategory(true)}>
                          <IconPencil size={14} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Select
                    id="categoryId"
                    name="categoryId"
                    value={values.categoryId}
                    onChange={handleChange}
                    fullWidth
                    displayEmpty
                    sx={{ marginTop: 1 }}
                  >
                    <MenuItem value="" disabled>
                      {isLoading || isFetching
                        ? "Carregando categorias..."
                        : "Selecione uma categoria"}
                    </MenuItem>

                    {Array.isArray(data) &&
                      data
                        .filter((c: any) => c?.status === true)
                        .map((c: any) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}

                    {!isLoading &&
                      !isFetching &&
                      Array.isArray(data) &&
                      data.filter((c: any) => c?.status === true).length ===
                        0 && (
                        <MenuItem value="" disabled>
                          Nenhuma categoria ativa
                        </MenuItem>
                      )}
                  </Select>
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Stack>
                  <Typography fontWeight={600} fontSize={14}>
                    Data de vencimento
                  </Typography>
                  <Input
                    id="dueDate"
                    name="dueDate"
                    type="date"
                    fullWidth
                    sx={{ marginTop: 1 }}
                    InputLabelProps={{ shrink: true }}
                    value={values.dueDate}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Checkbox
                    id="paidCheckbox"
                    checked={values.status === "PAID"}
                    onChange={(e) =>
                      setFieldValue(
                        "status",
                        e.target.checked ? "PAID" : "PENDING",
                      )
                    }
                    sx={{ padding: 0 }}
                  />
                  <Typography fontSize={14}>Já foi pago?</Typography>
                </Stack>
              </Grid2>
            </>
          )}

          {step === "3" && (
            <Grid2 size={{ xs: 12 }}>
              <Stack spacing={2}>
                <Typography fontWeight={600} fontSize={14}>
                  Tipo de Pagamento
                </Typography>

                <ButtonGroup fullWidth variant="outlined" color="primary">
                  <Button
                    variant={!values.isRecurring ? "contained" : "outlined"}
                    onClick={() => {
                      setFieldValue("isRecurring", false);
                      setFieldValue("recurrence", "");
                    }}
                  >
                    Única
                  </Button>
                  <Button
                    variant={values.isRecurring ? "contained" : "outlined"}
                    onClick={() => {
                      setFieldValue("isRecurring", true);
                      setFieldValue("recurrence", "MONTHLY");
                    }}
                  >
                    Recorrente
                  </Button>
                </ButtonGroup>

                {values.isRecurring && (
                  <>
                    <Typography fontWeight={600} fontSize={14}>
                      Frequência
                    </Typography>
                    <Select
                      id="recurrence"
                      name="recurrence"
                      value={values.recurrence}
                      onChange={handleChange}
                      fullWidth
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Selecione a frequência
                      </MenuItem>
                      <MenuItem value="MONTHLY">Mensal</MenuItem>
                      <MenuItem value="BIMONTHLY">Bimestral</MenuItem>
                      <MenuItem value="QUARTERLY">Trimestral</MenuItem>
                      <MenuItem value="SEMIANNUAL">Semestral</MenuItem>
                      <MenuItem value="ANNUAL">Anual</MenuItem>
                    </Select>
                  </>
                )}
              </Stack>
            </Grid2>
          )}

          <Grid2 size={{ xs: 12 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                if (step === "1") {
                  if (!values.title) {
                    toast.info("Preencha título");
                    return;
                  }

                  setStep("2");
                } else if (step === "2") {
                  if (!values.amount || !values.categoryId || !values.dueDate) {
                    toast.info("Preencha todos os campos financeiros");
                    return;
                  }
                  setStep("3");
                } else if (step === "3") {
                  handleSubmit();
                }
              }}
            >
              {step === "3" ? "Cadastrar" : "Próximo"}
            </Button>
          </Grid2>
        </Grid2>
      </DialogContent>

      <CategoryRegister
        open={openCategory}
        onClose={() => setOpenCategory(false)}
        refetch={refetchCategory}
      />

      <CategoryEdit
        open={openEditCategory}
        onClose={() => setEditCategory(false)}
        refetch={refetchCategory}
      />
    </Dialog>
  );
};

export default PayableRegister;
